/**
 * Onglet Aspects — Tableau fidele au PHP (aspects.php)
 * Colonnes : Planete 1 (symbole+nom+signe) | Aspect | Planete 2 | Orbe | Nature
 * Tri par orbe croissant, aspects exacts en premier
 */

import { useState } from 'react';
import { colors } from '../../styles/tokens.js';
import { PLANET_SYMBOLS, ASPECT_INFO } from './planetColors.js';
import type { ChartAnalysis } from '../../types/clients.js';

type FilterType = 'tous' | 'harmonie' | 'tension' | 'fusion' | 'mineur' | 'exact';

/** Couleurs par nature */
const NATURE_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  harmonie: { bg: '#dcfce7', text: '#166534', border: '#22c55e' },
  tension:  { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' },
  fusion:   { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' },
  neutre:   { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' },
  mineur:   { bg: '#fefce8', text: '#854d0e', border: '#eab308' },
};

/** Determine la nature d'un aspect (depuis la valeur renvoyee par l'API) */
function getAspectNature(nature: string): string {
  if (nature === 'harmonie') return 'harmonie';
  if (nature === 'tension') return 'tension';
  if (nature === 'mineur') return 'mineur';
  return 'fusion';
}

interface Props {
  aspects: ChartAnalysis['aspects'];
}

export function AspectsTab({ aspects }: Props) {
  const [filter, setFilter] = useState<FilterType>('tous');
  const [showHelp, setShowHelp] = useState(false);

  const stats = aspects.statistiques;
  const harmoniques = stats.harmoniques ?? 0;
  const tensions = stats.tensions ?? 0;
  const conjonctions = stats.conjonctions ?? 0;
  const mineurs = stats.mineurs ?? 0;

  const filteredAspects = aspects.aspects.filter((asp) => {
    if (filter === 'tous') return true;
    if (filter === 'exact') return asp.orbe < 2;
    return getAspectNature(asp.nature) === filter;
  });

  const filterBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 12px',
    borderRadius: 6,
    border: `1px solid ${active ? colors.accent : colors.border}`,
    background: active ? colors.accent : colors.bgPage,
    color: active ? colors.text : colors.textMid,
    fontSize: '0.85rem',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s',
  });

  return (
    <div style={{
      background: '#fff', borderRadius: 8, padding: '1.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1.25rem', paddingBottom: '0.75rem',
        borderBottom: `2px solid ${colors.border}`,
      }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', color: colors.text, fontWeight: 700 }}>
          <i className="bi bi-diagram-3-fill" style={{ color: colors.accent, marginRight: 8 }} />
          Tableau des Aspects
        </h3>
        <button
          onClick={() => setShowHelp(!showHelp)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 12px', fontSize: '0.8rem', fontWeight: 500,
            background: showHelp ? colors.accent : '#f3f4f6',
            color: showHelp ? '#fff' : colors.textMid,
            border: 'none', borderRadius: 6, cursor: 'pointer',
            transition: 'all 0.2s', fontFamily: 'inherit',
          }}
        >
          <i className="bi bi-question-circle" />
          Aide
        </button>
      </div>

      {/* Aide */}
      {showHelp && (
        <div style={{
          background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
          padding: '1.25rem', marginBottom: '1.25rem', fontSize: '0.85rem',
          lineHeight: 1.7, color: '#1e3a5f',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0c4a6e' }}>
              Comprendre les Aspects
            </h4>
            <button
              onClick={() => setShowHelp(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#64748b' }}
            >
              &times;
            </button>
          </div>
          <p style={{ margin: '0 0 8px' }}>Un aspect est un <strong>angle significatif</strong> entre deux planetes. Il indique comment leurs energies interagissent.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div><strong>☌ Conjonction (0°)</strong> — Fusion : les energies se melangent et s'intensifient.</div>
            <div><strong>☍ Opposition (180°)</strong> — Tension : polarite, tiraillement, equilibre a trouver.</div>
            <div><strong>△ Trigone (120°)</strong> — Harmonie : fluidite naturelle, talents innes.</div>
            <div><strong>□ Carre (90°)</strong> — Tension : friction dynamique, pousse a l'action.</div>
            <div><strong>⚹ Sextile (60°)</strong> — Harmonie : opportunites a saisir, communication facile.</div>
            <div><strong>⚻ Quinconce (150°)</strong> — Tension : ajustement entre energies incompatibles.</div>
          </div>
          <p style={{ margin: '8px 0 4px', fontWeight: 600, color: '#854d0e' }}>Aspects mineurs :</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
            <div><strong>⚺ Semi-Sextile (30°)</strong> — Liaison subtile entre signes voisins.</div>
            <div><strong>∠ Semi-Carre (45°)</strong> — Irritation legere, friction secondaire.</div>
            <div><strong>⊼ Sesqui-Carre (135°)</strong> — Tension mineure, ajustement requis.</div>
          </div>
          <p style={{ margin: '8px 0 0', fontStyle: 'italic' }}>
            ⭐ Un aspect <strong>exact</strong> (orbe {'<'} 2°) a une influence particulierement puissante.
          </p>
        </div>
      )}

      {/* Statistiques + filtres — layout PHP */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem',
      }}>
        {/* Compteurs */}
        <div style={{ display: 'flex', gap: '1rem' }}>
          <div style={{ padding: '0.5rem 1rem', background: '#f0fdf4', borderRadius: 8, borderLeft: '3px solid #22c55e' }}>
            <span style={{ fontSize: '0.8rem', color: '#166534' }}>Harmoniques</span>
            <strong style={{ display: 'block', fontSize: '1.2rem', color: '#166534' }}>{harmoniques}</strong>
          </div>
          <div style={{ padding: '0.5rem 1rem', background: '#fef2f2', borderRadius: 8, borderLeft: '3px solid #ef4444' }}>
            <span style={{ fontSize: '0.8rem', color: '#991b1b' }}>Tensions</span>
            <strong style={{ display: 'block', fontSize: '1.2rem', color: '#991b1b' }}>{tensions}</strong>
          </div>
          <div style={{ padding: '0.5rem 1rem', background: '#eff6ff', borderRadius: 8, borderLeft: '3px solid #3b82f6' }}>
            <span style={{ fontSize: '0.8rem', color: '#1e40af' }}>Fusions</span>
            <strong style={{ display: 'block', fontSize: '1.2rem', color: '#1e40af' }}>{conjonctions}</strong>
          </div>
          {mineurs > 0 && (
            <div style={{ padding: '0.5rem 1rem', background: '#fefce8', borderRadius: 8, borderLeft: '3px solid #eab308' }}>
              <span style={{ fontSize: '0.8rem', color: '#854d0e' }}>Mineurs</span>
              <strong style={{ display: 'block', fontSize: '1.2rem', color: '#854d0e' }}>{mineurs}</strong>
            </div>
          )}
        </div>

        {/* Filtres */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem', color: colors.textMuted }}>Filtrer :</span>
          <button onClick={() => setFilter('tous')} style={filterBtnStyle(filter === 'tous')}>Tous</button>
          <button onClick={() => setFilter('harmonie')} style={filterBtnStyle(filter === 'harmonie')}>△ ⚹</button>
          <button onClick={() => setFilter('tension')} style={filterBtnStyle(filter === 'tension')}>□ ☍ ⚻</button>
          <button onClick={() => setFilter('fusion')} style={filterBtnStyle(filter === 'fusion')}>☌</button>
          <button onClick={() => setFilter('mineur')} style={filterBtnStyle(filter === 'mineur')}>Mineurs</button>
          <button onClick={() => setFilter('exact')} style={filterBtnStyle(filter === 'exact')}>Exacts</button>
        </div>
      </div>

      {/* Tableau des aspects — style PHP */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Planete 1</th>
              <th style={{ ...thStyle, textAlign: 'center', width: '15%' }}>Aspect</th>
              <th style={thStyle}>Planete 2</th>
              <th style={{ ...thStyle, textAlign: 'center', width: '12%' }}>Orbe</th>
              <th style={{ ...thStyle, textAlign: 'center', width: '15%' }}>Nature</th>
            </tr>
          </thead>
          <tbody>
            {filteredAspects.map((asp, i) => {
              const nature = getAspectNature(asp.nature);
              const nStyle = NATURE_STYLES[nature] ?? NATURE_STYLES['fusion']!;
              const info = ASPECT_INFO[asp.type];
              const isExact = asp.orbe < 2;
              const isMineur = nature === 'mineur';

              return (
                <tr
                  key={i}
                  style={{
                    borderBottom: `1px solid ${colors.border}`,
                    opacity: isMineur ? 0.7 : 1,
                    fontStyle: isMineur ? 'italic' : 'normal',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgPage; e.currentTarget.style.opacity = '1'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.opacity = isMineur ? '0.7' : '1'; }}
                >
                  {/* Planete 1 */}
                  <td style={tdStyle}>
                    <span style={symbolStyle}>{PLANET_SYMBOLS[asp.planete1] ?? ''}</span>
                    {asp.planete1}
                    {asp.signe1 && (
                      <span style={{ color: colors.textMuted, fontSize: '0.8rem', marginLeft: 6 }}>{asp.signe1}</span>
                    )}
                  </td>

                  {/* Aspect (symbole + nom) */}
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span style={{
                      fontSize: '1.3rem',
                      fontFamily: "'Noto Sans Symbols 2', sans-serif",
                      color: info?.color ?? colors.textMid,
                    }}>
                      {info?.symbol ?? asp.type}
                    </span>
                    <div style={{ fontSize: '0.75rem', color: colors.textMuted }}>{asp.type}</div>
                  </td>

                  {/* Planete 2 */}
                  <td style={tdStyle}>
                    <span style={symbolStyle}>{PLANET_SYMBOLS[asp.planete2] ?? ''}</span>
                    {asp.planete2}
                    {asp.signe2 && (
                      <span style={{ color: colors.textMuted, fontSize: '0.8rem', marginLeft: 6 }}>{asp.signe2}</span>
                    )}
                  </td>

                  {/* Orbe */}
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span style={isExact ? { fontWeight: 700, color: '#7c3aed' } : {}}>
                      {asp.orbe.toFixed(1)}°
                      {isExact && (
                        <i className="bi bi-star-fill" style={{ color: '#7c3aed', fontSize: '0.7rem', marginLeft: 4 }} />
                      )}
                    </span>
                  </td>

                  {/* Nature (badge) */}
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block', padding: '4px 12px', borderRadius: 12,
                      fontSize: '0.8rem', fontWeight: 500,
                      background: nStyle.bg, color: nStyle.text,
                      border: `1px solid ${nStyle.border}`,
                    }}>
                      {nature.charAt(0).toUpperCase() + nature.slice(1)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredAspects.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: colors.textMuted }}>
          <i className="bi bi-inbox" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }} />
          Aucun aspect pour ce filtre.
        </div>
      )}
    </div>
  );
}

/** Styles du tableau */
const thStyle: React.CSSProperties = {
  padding: '12px 8px',
  textAlign: 'left',
  background: colors.bgPage,
  borderBottom: `2px solid ${colors.border}`,
  fontWeight: 600,
  fontSize: '0.85rem',
  color: colors.text,
};

const tdStyle: React.CSSProperties = {
  padding: '10px 8px',
  fontSize: '0.9rem',
  color: colors.text,
};

const symbolStyle: React.CSSProperties = {
  fontFamily: "'Noto Sans Symbols 2', sans-serif",
  fontSize: '1.1rem',
  marginRight: 6,
};
